import { AlertCircle, AlertTriangle, CheckCircle } from "lucide-react";

const findings = [
  {
    id: 1,
    status: "RED",
    heading: "Supplier freight missing",
    description: "The selected supplier quote excludes freight, while the estimate carries only the quoted material subtotal. Action: obtain a delivered quotation or add an explicit freight allowance.",
    icon: AlertCircle,
    color: "red",
  },
  {
    id: 2,
    status: "RED",
    heading: "Temporary access not clearly priced",
    description: "The proposed methodology requires heavy access, but construction, maintenance, removal and reinstatement have no clear cost origin. Action: price or qualify the exposure before submission.",
    icon: AlertCircle,
    color: "red",
  },
  {
    id: 3,
    status: "AMBER",
    heading: "Margin/markup mismatch",
    description: "The commercial sheet labels 10% as margin, but the formula applies 10% markup. Action: confirm the intended profitability method and correct the calculation.",
    icon: AlertTriangle,
    color: "amber",
  },
];

const statusStyles = {
  RED: {
    bg: "bg-red-50",
    border: "border-red-600",
    badge: "bg-red-600 text-white",
    icon: "text-red-600",
  },
  AMBER: {
    bg: "bg-amber-50",
    border: "border-amber-500",
    badge: "bg-amber-500 text-black",
    icon: "text-amber-500",
  },
  GREEN: {
    bg: "bg-green-50",
    border: "border-green-600",
    badge: "bg-green-600 text-white",
    icon: "text-green-600",
  },
};

export default function ExampleFindings() {
  return (
    <section
      id="example-findings"
      className="py-16 md:py-24 border-t-4 border-black bg-white"
      data-testid="example-findings-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section header */}
        <div className="mb-12 md:mb-16">
          <span className="font-mono text-sm uppercase tracking-widest text-zinc-500">
            Example Findings
          </span>
          <h2
            className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl uppercase mt-4"
            data-testid="example-findings-title"
          >
            What We Catch
          </h2>
          <p className="font-body text-zinc-600 mt-4 max-w-2xl">
            Sample findings demonstrating the types of issues identified during a Tender Red Review. These are illustrative examples, not completed client results.
          </p>
        </div>

        {/* Finding cards */}
        <div className="grid md:grid-cols-3 gap-6">
          {findings.map((finding) => {
            const styles = statusStyles[finding.status];
            return (
              <div
                key={finding.id}
                className={`p-6 border-l-4 ${styles.border} ${styles.bg}`}
                data-testid={`finding-${finding.id}`}
              >
                {/* Status badge */}
                <div className="flex items-center gap-3 mb-4">
                  <span
                    className={`px-3 py-1 font-mono text-xs uppercase tracking-wider ${styles.badge}`}
                  >
                    {finding.status}
                  </span>
                  <finding.icon size={20} className={styles.icon} />
                </div>

                {/* Content */}
                <h3 className="font-heading font-bold text-lg uppercase mb-3">
                  {finding.heading}
                </h3>
                <p className="font-body text-zinc-700 text-sm leading-relaxed">
                  {finding.description}
                </p>
              </div>
            );
          })}
        </div>

        {/* Disclaimer */}
        <div className="mt-8 text-center">
          <p className="font-mono text-xs text-zinc-500 uppercase tracking-wider">
            Example findings for illustration only. Actual findings depend on the specific tender reviewed.
          </p>
        </div>
      </div>
    </section>
  );
}
